import React, { useCallback, useEffect } from "react";
import { SimpleDialog } from "../dialogs/SimpleDialog";
import { FormProvider, useForm } from "react-hook-form";
import {
  AreaUnit,
  CompositeAsset,
  CompositeAssetPreset,
  CompositeAssetRequest,
  CompositePresetType,
} from "../../types/api.types";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormSelect } from "../form/FormSelect";
import { DesignationType } from "common/types/comparableFields.types";
import { getDesignationTypeTranslation } from "../../helpers/comparableFields.helpers";
import { FormInput } from "common/components/form/hookForms/FormInput";
import { createCompositeAsset, updateCompositeAsset } from "../../api/api";
import { showToastError } from "common/toast/toast";
import { getErrorMessage } from "common/helpers/error.helpers";
import { useMutation, useQueryClient } from "react-query";
import { FormSettingsContext } from "common/components/form/hookForms/FormSettingsContext";
import { FormColumns } from "common/components/form/layout/FormColumns";
import { FormFractionInput } from "common/components/form/hookForms/FormFractionInput";
import colors from "common/styles/colors";
import { round } from "common/helpers/common.helpers";
import { ParkingPriceInfo } from "./ParkingPriceInfo";
import { StoragePriceInfo } from "./StoragePriceInfo";
import { useTranslation } from "react-i18next";
import { useProperty } from "../../hooks/property.hooks";
import { getUniqueNrSchema } from "common/helpers/form.helpers";

interface CompositeAssetsFormProps {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  compositeAsset?: CompositeAsset;
  isCustomer?: boolean;
  hideValue?: boolean;
  preset?: CompositeAssetPreset | null;
}

const createValidationSchema = (isValueRequired: boolean) => {
  const schema: Yup.ObjectSchema<CompositeAssetRequest> = Yup.object({
    propertyId: Yup.string().required(),
    name: Yup.string().required(),
    designationTypeId: Yup.string().required(),
    uniqueNr: getUniqueNrSchema(),
    areaTotal: Yup.number().required(),
    areaPurchased: Yup.number().required(),
    areaUnit: Yup.string().oneOf(Object.values(AreaUnit)).required(),
    assetPart1: Yup.number().required(),
    assetPart2: Yup.number().required(),
    isCustomer: Yup.boolean().required(),
    value: isValueRequired
      ? Yup.number().when("isCustomer", {
          is: true,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        })
      : Yup.number(),
    address: Yup.string(),
  }).test(
    "value-check",
    "Value should not exist if isCustomer is true",
    function (value) {
      if (value.isCustomer && value.value !== undefined) {
        return false; // invalid if isCustomer is true and value exists
      }
      return true;
    }
  );
  return schema;
};

export const getInitialCompositeAssetWithPurchasedArea = (
  compositeAsset: Partial<CompositeAssetRequest>
) => {
  if (compositeAsset.areaPurchased) {
    return compositeAsset;
  }

  if (
    compositeAsset.areaTotal &&
    compositeAsset.assetPart1 &&
    compositeAsset.assetPart2
  ) {
    return {
      ...compositeAsset,
      areaPurchased:
        (compositeAsset.areaTotal / compositeAsset.assetPart2) *
        compositeAsset.assetPart1,
    };
  }

  return compositeAsset;
};

const getCompositeAssetWithAreaTotal = (
  compositeAsset: Partial<CompositeAssetRequest>
) => {
  if (
    compositeAsset.areaPurchased &&
    compositeAsset.assetPart1 &&
    compositeAsset.assetPart2
  ) {
    return {
      ...compositeAsset,
      areaTotal:
        (compositeAsset.areaPurchased / compositeAsset.assetPart1) *
        compositeAsset.assetPart2,
    };
  } else {
    return {
      ...compositeAsset,
      areaTotal: 0,
    };
  }
};

export const CompositeAssetsForm: React.FC<CompositeAssetsFormProps> = ({
  open,
  onClose,
  propertyId,
  compositeAsset,
  isCustomer,
  hideValue,
  preset,
}) => {
  const { t } = useTranslation();

  const { data: property } = useProperty(propertyId);

  const defaultValues = {
    propertyId,
    areaUnit: AreaUnit.SqMeters,
    isCustomer: isCustomer ?? false,
    assetPart1: 1,
    assetPart2: 1,
    ...preset,
  };

  const methods = useForm<CompositeAssetRequest>({
    resolver: yupResolver(createValidationSchema(!hideValue)),
    defaultValues,
  });

  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(
    compositeAsset
      ? (data: CompositeAssetRequest) =>
          updateCompositeAsset(compositeAsset._id, data)
      : createCompositeAsset,
    {
      onSuccess: () => {
        queryClient.invalidateQueries("compositeAssets");
        queryClient.invalidateQueries("report_price");
        onClose();
      },
      onError: (error) => {
        showToastError(getErrorMessage(error));
      },
    }
  );

  useEffect(() => {
    if (open) {
      if (compositeAsset) {
        methods.reset(
          getInitialCompositeAssetWithPurchasedArea({
            ...compositeAsset,
            isCustomer: isCustomer ?? false,
          })
        );
      } else {
        methods.reset(defaultValues);
      }
    }
  }, [open, compositeAsset]);

  const data = methods.watch();

  useEffect(() => {
    const newData = getCompositeAssetWithAreaTotal(data);

    methods.reset(newData);
  }, [data.areaPurchased, data.assetPart1, data.assetPart2]);

  const desinationTypeId = methods.watch("designationTypeId");

  const areaMessage = t("propertyDetails.compositeAssets.form.allArea", {
    area: round(data.areaTotal, 2),
    unit: data.areaUnit,
  });

  return (
    <SimpleDialog
      title={t(
        compositeAsset
          ? "propertyDetails.compositeAssets.form.editTitle"
          : "propertyDetails.compositeAssets.form.addTitle"
      )}
      open={open}
      onClose={onClose}
      onSubmit={methods.handleSubmit((data) => mutate(data))}
      isLoading={isLoading}
    >
      <FormProvider {...methods}>
        <FormSettingsContext.Provider
          value={{ smallFont: true, maxInputWidth: 350, inputHeight: 56 }}
        >
          <form
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
          >
            <FormColumns>
              <FormInput
                name="name"
                label={t("propertyDetails.compositeAssets.form.name")}
                variant="text"
                isRequired
              />

              <FormSelect
                values={Object.values(DesignationType).map((value) => value)}
                getTitle={(value) =>
                  getDesignationTypeTranslation(value as DesignationType)
                }
                name="designationTypeId"
                label={t("propertyDetails.compositeAssets.form.type")}
                isRequired
              />
            </FormColumns>
            <FormColumns>
              <FormInput
                name="uniqueNr"
                label={t("propertyDetails.compositeAssets.form.uniqueNumber")}
                variant="text"
                isRequired
              />
              <FormInput
                name="address"
                label={t("propertyDetails.compositeAssets.form.address")}
                variant="text"
              />
            </FormColumns>
            <FormColumns>
              <div
                style={{ display: "flex", flexDirection: "row", gap: "15px" }}
              >
                <div
                  style={{
                    width: "150px",
                  }}
                >
                  <FormInput
                    name="areaPurchased"
                    label={t(
                      "propertyDetails.compositeAssets.form.purchasedArea"
                    )}
                    variant="decimal"
                    isRequired
                  />
                  <div
                    style={{
                      color: colors.grey,
                      fontSize: "15px",
                      marginTop: "5px",
                    }}
                  >
                    {areaMessage}
                  </div>
                </div>
                <div
                  style={{
                    width: "80px",
                  }}
                >
                  <FormSelect
                    name="areaUnit"
                    label={t("propertyDetails.compositeAssets.form.units")}
                    values={Object.values(AreaUnit).map((value) => value)}
                    disabled={compositeAsset?.isPlotArea}
                  />
                </div>
              </div>

              <FormFractionInput
                name1="assetPart1"
                name2="assetPart2"
                label={t("propertyDetails.compositeAssets.form.valuedPart")}
                isRequired
              />
            </FormColumns>
            {!isCustomer && !hideValue && (
              <FormInput
                name="value"
                label={t("propertyDetails.compositeAssets.form.marketPrice")}
                units="€"
                variant="number"
                maxWidth={250}
                isRequired
              />
            )}
            {!isCustomer &&
              !hideValue &&
              !!property &&
              (desinationTypeId === DesignationType.Garage ||
                desinationTypeId ===
                  DesignationType.OtherEngineeringStructures) && (
                <div style={{ marginTop: "-10px" }}>
                  <ParkingPriceInfo
                    property={{
                      lat: property.lat,
                      lng: property.lng,
                    }}
                    selectedType={
                      preset?.presetType ??
                      (desinationTypeId === DesignationType.Garage
                        ? CompositePresetType.ParkingUnderground
                        : desinationTypeId ===
                          DesignationType.OtherEngineeringStructures
                        ? CompositePresetType.ParkingOverground
                        : undefined)
                    }
                  />
                </div>
              )}

            {!isCustomer &&
              !hideValue &&
              !!property &&
              desinationTypeId === DesignationType.OtherSubFarm && (
                <div style={{ marginTop: "-10px" }}>
                  <StoragePriceInfo
                    property={{
                      lat: property.lat,
                      lng: property.lng,
                    }}
                  />
                </div>
              )}
          </form>
        </FormSettingsContext.Provider>
      </FormProvider>
    </SimpleDialog>
  );
};
